import { differenceInSeconds, format, parseISO } from 'date-fns';
import { IPeople } from '../models';

export const formatInputPhoneNumber = (val: string | undefined) => {
  let number = val?.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
  // taken from here, https://stackoverflow.com/questions/17651207/mask-us-phone-number-string-with-javascript
  return (
    number &&
    (!number[2] ? number[1] : `(${number[1]}) ${number[2]}${number[3] ? `-${number[3]}` : ''}`)
  );
};

/**
 * Formats a string or number into USD
 * @example formatMoney(1) => $1.00
 * @example formatMoney(0) => $0.00
 * @example formatMoney('1.99') => $1.99
 * @example formatMoney('9999.99') => $9,999.99
 * @example formatMoney(undefined) => $0.00
 * @example formatMoney(null) => $0.00
 * @param value number | string | undefined | null
 * @param digits number | undefined
 * @returns string
 */
export const formatMoney = (value: number | string | undefined | null, digits = 2): string => {
  let amount = 0;

  if (value) {
    if (typeof value === 'string') {
      // strip out any commas or dollar signs so that $9,999.99 passes !isNaN test
      value =
        value.includes(',') || value.includes('$')
          ? value.replace(',', '').replace('$', '')
          : value;
      // make sure the string is a number
      if (!isNaN(value as unknown as number)) {
        amount = Number(value);
      }
    } else if (typeof value === 'number') {
      amount = value;
    }
  }

  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/NumberFormat
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

/**
 * Format date string to Date
 * @example "2020-06-09T00:00:00+00:00" => Tue Jun 09 2020 00:00:00 GMT-0400
 * @param date string
 * @returns Date
 */
export const stringToDate = (date: string): Date => {
  const d = date.split('T')[0].split('-');
  // Months are 0 index so subtract 1
  return new Date(+d[0], +d[1] - 1, +d[2]);
};

/**
 * Format date
 * @example 1/6/2020
 * @example 12/12/2020
 * @param date Date | number | string
 * @returns string
 */
export const formatDate = (date: Date | number | string | null): string | null => {
  if (date) {
    const stringDate: Date | number = typeof date === 'string' ? stringToDate(date) : date;
    return format(stringDate, 'L/d/yyyy');
  }
  return null;
};

/**
 * Format date for urls and files
 * @example 1-6-2020
 * @example 12-12-2020
 * @param date Date | number | string
 * @returns string
 */
export const formatFileSafeDate = (date: Date | number | string | null): string | null => {
  if (date) {
    const stringDate: Date | number = typeof date === 'string' ? stringToDate(date) : date;
    return format(stringDate, 'L-d-yyyy');
  }
  return null;
};
/**
 * Format date into a short friendly date with time
 * @example 1/6/2020 2:00pm
 * @example 12/12/2020 12:00am
 * @param date Date | number | string
 * @returns string
 */
export const formatShortFriendlyDateWithTime = (date: Date | number | string): string => {
  if (date) {
    const parsedDate: Date | number = typeof date === 'string' ? parseISO(date) : date;
    return format(parsedDate, 'L/d/yyyy h:mm a');
  }
  return null;
};

export const formatShortFriendlyTime = (date: Date | number | string): string => {
  if (date) {
    const parsedDate: Date | number = typeof date === 'string' ? parseISO(date) : date;
    return format(parsedDate, 'h:mm a');
  }
  return null;
};

/**
 * gets the time in localized to the central timezone
 * @param date
 * @returns
 */
export const formatShortFriendlyTimeInCentral = (date: Date | number | string): string => {
  if (date) {
    const parsedDate: Date | number = typeof date === 'string' ? parseISO(date) : date;
    return new Date(parsedDate).toLocaleTimeString('en-US', { timeZone: 'America/Chicago' });
  }
};

/**
 * Format date into a short friendly date with time and seconds
 * @example 1/6/2020 2:00:00pm
 * @example 12/12/2020 12:00:00AM
 * @param date Date | number | string
 * @returns string
 */
export const formatDateWithTimeAndSeconds = (date: Date | number | string): string => {
  if (date) {
    const parsedDate: Date | number = typeof date === 'string' ? parseISO(date) : date;
    return format(parsedDate, 'L/d/yyyy h:mm:ssa');
  }
  return null;
};

/**
 * return the difference in seconds between two date objects
 * @param date Date | number | string
 * @returns string
 */
export const getDifferenceBetweenTwoDatesInSeconds = (
  date1: Date | number | string,
  date2: Date | number | string
): number => {
  if (date1 && date2) {
    const parsedDate1: Date | number = typeof date1 === 'string' ? parseISO(date1) : date1;
    const parsedDate2: Date | number = typeof date2 === 'string' ? parseISO(date2) : date2;
    return differenceInSeconds(parsedDate2, parsedDate1);
  }
  return null;
};

export const formatDBFriendlyDateTime = (date: Date | number | string): string => {
  if (date) {
    const dateString = date.toLocaleString();
    const dateStringWithoutTimezone = dateString.split(' GMT')[0];
    const updatedDate = new Date(dateStringWithoutTimezone);
    const formattedDateString = format(updatedDate, "yyyy-MM-dd'T'HH:mm:ss");
    return formattedDateString;
  }
  return null;
};

export const formatArrayParams = (data: string[], key: string) => {
  return `${data.map((id, index) => `${index !== 0 ? '&' : ''}${key}=${id}`).join('')}`;
};

/** @example `Shopping cart contains ${items.length} item${s(items.length)}.` */
export const checkArrayLengthReturnS = (arr: unknown[]): string => (arr.length > 1 ? 's' : '');

export const groupByKey = (array: any[], key: string) => {
  return array.reduce((hash, obj) => {
    if (obj[key] === undefined) return hash;
    return Object.assign(hash, { [obj[key]]: (hash[obj[key]] || []).concat(obj) });
  }, {});
};

export const formatPascalStringToSpaces = (data: string) => {
  const regx = /([A-Z])([A-Z])([a-z])|([a-z])([A-Z])/g;
  return data.replace(regx, '$1$4 $2$3$5');
};

export const decimalRegExp = /^[+-]?(\d+([.]\d*)?|[.]\d+)$/;
export const decimalLengthFourRegExp = /^\d*(\.)?\d{0,4}$/;

export const unescape = (string: string) => {
  return new DOMParser().parseFromString(string, 'text/html').querySelector('html').textContent;
};

export const formatPeopleWorkplaceName = (user: IPeople) => {
  return `${user?.firstName} ${user?.lastName}${
    user?.partners?.[0] ? ` - ${user?.partners?.[0].partnerName}` : ''
  }`;
};

export const unCamelCase = (str: string) => {
  return (
    str
      // insert a space between lower & upper
      ?.replace(/([a-z])([A-Z])/g, '$1 $2')
      // space before last upper in a sequence followed by lower
      ?.replace(/\b([A-Z]+)([A-Z])([a-z])/, '$1 $2$3')
      // uppercase the first character
      ?.replace(/^./, function (str) {
        return str.toUpperCase();
      })
  );
};

export const formatCapitalCase = (text: string) => {
  return (
    text
      ?.toLowerCase()
      ?.split(' ')
      ?.map(word => word?.charAt(0)?.toUpperCase() + word?.substring(1))
      ?.join(' ') ?? ''
  );
};

export const getTextBeforeHypen = (text: string) => {
  return text?.split('-')?.[0]?.trim() ?? '';
};

export const isTextOverflowing = (text: string) => {
  return text?.length > 50;
};
export const formatEllipsis = (text: string) => {
  if (!text) return text;
  return isTextOverflowing(text) ? text.substring(0, 47) + '...' : text;
};

export const getOrdinalNumber = (n: number) => {
  let s = ['th', 'st', 'nd', 'rd'];
  let v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export const camelCaseToWords = (s: string) => {
  const result = s.replace(/([A-Z])/g, ' $1');
  return result.charAt(0).toUpperCase() + result.slice(1);
};

export const formatAddress = ({
  address1,
  address2,
  city,
  state,
  zip
}: {
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
}): string => {
  return `${address1 ?? ''} ${address2 ?? ''} ${city ?? ''}${city && state ? `, ${state}` : ''} ${
    state && !city ? state : ''
  } ${zip ?? ''}`;
};
